import React, { createContext, useState, useContext, useEffect } from 'react';
import { authService } from '../services/api';
import { clearSession, addSessionLostHandler } from '../services/authTokens';

interface User {
  id: number;
  name: string;
  email: string;
  role: 'admin' | 'manager' | 'viewer';
}

interface AuthContextData {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Restaura sessão: o /auth/me sai com um access token obtido a partir do
    // cookie httpOnly de refresh. O localStorage guarda só o perfil (nome,
    // permissões) para pintar a UI na hora — nunca credencial.
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      const parsed = JSON.parse(savedUser);
      // Don't restore session if MFA setup is pending
      if (!parsed.mfaSetupRequired) {
        setUser(parsed);
      }
    }

    // Leva a UI de volta ao login quando a sessão morrer em qualquer aba.
    const unsubscribeSessionLost = addSessionLostHandler(() => {
      setUser(null);
      localStorage.removeItem('user');
    });

    authService.getMe()
      .then((userData) => {
        // If MFA setup is required, don't authenticate — force login flow
        if (userData?.mfaSetupRequired) {
          setUser(null);
          localStorage.removeItem('user');
          clearSession();
          return;
        }
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
      })
      .catch((error) => {
        // Only clear session on auth errors (401/403), not on temporary errors (429, network, etc.)
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          setUser(null);
          localStorage.removeItem('user');
        }
        // For temporary errors (429, 500, network), keep the saved user from localStorage
      })
      .finally(() => setLoading(false));

    return unsubscribeSessionLost;
  }, []);

  const login = async (email: string, password: string) => {
    const userData = await authService.login(email, password);
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const logout = async () => {
    try { await authService.logout(); } catch { /* ignore */ }
    setUser(null);
    localStorage.removeItem('user');
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
